import React, { ChangeEvent, FC, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Icon from '@material-ui/core/Icon';
import Chip from '@material-ui/core/Chip';
import Tooltip from '@material-ui/core/Tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';

// Frontend-only mock of the hospital Installation module. All state is local —
// no data is persisted and no documents are actually generated.

interface SectionField {
  key: string;
  label: string;
  type?: 'text' | 'date';
  multiline?: boolean;
}

interface SectionConfig {
  id: string;
  name: string;
  description: string;
  fields: SectionField[];
  hasPhotos: boolean;
}

const sections: SectionConfig[] = [
  {
    id: 'siteInformation',
    name: 'Site Information',
    description: 'Contact and key dates for this installation. Name, address, and phone come from the hospital record.',
    hasPhotos: false,
    fields: [
      { key: 'administrator', label: 'Administrator' },
      { key: 'email', label: 'Administrator Email' },
      { key: 'installationStart', label: 'Installation Start', type: 'date' },
      { key: 'installationEnd', label: 'Installation End', type: 'date' },
      { key: 'postInstallation', label: 'Post-Installation', type: 'date' },
      { key: 'launchDate', label: 'Launching Date', type: 'date' },
    ],
  },
  {
    id: 'floorPlan',
    name: 'Floor Plan',
    description: 'Upload the designed floor plan as an image or PDF.',
    hasPhotos: true,
    fields: [{ key: 'notes', label: 'Notes', multiline: true }],
  },
  {
    id: 'ispRouter',
    name: 'ISP & Router',
    description: 'Record the internet service and router details, and add photos of the installed router.',
    hasPhotos: true,
    fields: [
      { key: 'provider', label: 'Provider' },
      { key: 'accountNumber', label: 'Account #' },
      { key: 'speed', label: 'Speed' },
      { key: 'monthlyCost', label: 'Monthly Cost' },
      { key: 'ssid', label: 'SSID' },
      { key: 'password', label: 'Password' },
      { key: 'ip', label: 'IP' },
      { key: 'supportContact', label: 'Support Contact' },
    ],
  },
  {
    id: 'switch',
    name: 'Switch',
    description: 'Add photos of the installed switch and record its details.',
    hasPhotos: true,
    fields: [
      { key: 'location', label: 'Location' },
      { key: 'model', label: 'Model' },
      { key: 'notes', label: 'Notes', multiline: true },
    ],
  },
  {
    id: 'accessPoints',
    name: 'Access Points',
    description: 'Record where the access points are installed and add photos of each.',
    hasPhotos: true,
    fields: [{ key: 'locations', label: 'Locations', multiline: true }],
  },
  {
    id: 'gateways',
    name: 'Gateways',
    description: 'Record where the gateways are installed and add photos of each.',
    hasPhotos: true,
    fields: [{ key: 'locations', label: 'Locations', multiline: true }],
  },
  {
    id: 'tvs',
    name: 'TVs',
    description: 'Record where the TVs are installed and add photos of each.',
    hasPhotos: true,
    fields: [{ key: 'locations', label: 'Locations', multiline: true }],
  },
  {
    id: 'ipads',
    name: 'iPads',
    description: 'Record where the iPads are assigned and add photos.',
    hasPhotos: true,
    fields: [{ key: 'assignments', label: 'Assignments', multiline: true }],
  },
  {
    id: 'temperatureHumidity',
    name: 'Temperature & Humidity',
    description: 'Record where the temperature and humidity sensors are installed and add photos.',
    hasPhotos: true,
    fields: [{ key: 'locations', label: 'Sensor Locations', multiline: true }],
  },
];

const useStyles = makeStyles(theme => ({
  nav: {
    [theme.breakpoints.up('md')]: {
      width: '18rem',
      flexShrink: 0,
      borderRight: `1px solid ${theme.palette.divider}`,
    },
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25, 2),
    cursor: 'pointer',
    transition: 'background-color .15s ease-in-out',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  navItemSelected: {
    backgroundColor: theme.palette.action.selected,
  },
  navCheck: {
    color: theme.palette.success.main,
  },
  content: {
    flex: 1,
    minWidth: 0,
    padding: theme.spacing(2, 3),
  },
  dropzone: {
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color .15s ease-in-out',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  thumbnail: {
    position: 'relative',
    width: theme.spacing(14),
    height: theme.spacing(14),
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.default,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  thumbnailImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  thumbnailDelete: {
    position: 'absolute',
    top: theme.spacing(0.5),
    right: theme.spacing(0.5),
    padding: theme.spacing(0.25),
    backgroundColor: theme.palette.background.paper,
  },
  thumbnailLabel: {
    width: theme.spacing(14),
  },
}));

interface Photo {
  name: string;
  url: string;
  isPdf: boolean;
  label: string;
}

interface Props {
  hospital: any;
}

const HospitalInstallation: FC<Props> = ({ hospital }) => {
  const classes = useStyles();
  const [selectedId, setSelectedId] = useState(sections[0].id);
  const [values, setValues] = useState<Record<string, Record<string, string>>>({});
  const [photos, setPhotos] = useState<Record<string, Photo[]>>({});
  const [generating, setGenerating] = useState(false);
  const [generatedAt, setGeneratedAt] = useState<Date | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const selectedIndex = sections.findIndex(section => section.id === selectedId);
  const selected = sections[selectedIndex];
  const selectedPhotos = photos[selected.id] ?? [];

  const handleFieldChange = (sectionId: string, fieldKey: string) => (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setValues(prev => ({ ...prev, [sectionId]: { ...prev[sectionId], [fieldKey]: value } }));
  };

  const handleAddPhotos = (e: ChangeEvent<HTMLInputElement>) => {
    const added = Array.from(e.target.files ?? []).map(file => ({
      name: file.name,
      url: URL.createObjectURL(file),
      isPdf: file.type.indexOf('pdf') > -1,
      label: '',
    }));
    if (added.length > 0) {
      setPhotos(prev => ({ ...prev, [selected.id]: [...(prev[selected.id] ?? []), ...added] }));
    }
    e.target.value = '';
  };

  const handleRemovePhoto = (index: number) => {
    setPhotos(prev => ({ ...prev, [selected.id]: (prev[selected.id] ?? []).filter((_, i) => i !== index) }));
  };

  const handlePhotoLabelChange = (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
    const label = e.target.value;
    setPhotos(prev => ({
      ...prev,
      [selected.id]: (prev[selected.id] ?? []).map((photo, i) => (i === index ? { ...photo, label } : photo)),
    }));
  };

  const sectionStarted = (section: SectionConfig) =>
    Object.values(values[section.id] ?? {}).some(value => !!value) || (photos[section.id] ?? []).length > 0;

  const startedCount = sections.filter(sectionStarted).length;

  const handleGenerate = () => {
    setGenerating(true);
    // Mock only — the real implementation would generate the PDF from the captured data
    setTimeout(() => {
      setGenerating(false);
      setGeneratedAt(new Date());
    }, 1400);
  };

  return (
    <Box flex={1} display="flex" flexDirection="column">
      <Box
        mb={2}
        px={1}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gridGap="0.75rem"
      >
        <Box display="flex" alignItems="center" gridGap="0.75rem">
          <Chip size="small" label={`${startedCount} / ${sections.length}`} />
          <Typography variant="body2" color="textSecondary">
            Capture installation details and photos for {hospital?.name ?? 'this organization'}. Everything is stored
            here and the acceptance document can be generated at any time.
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gridGap="0.75rem">
          {generatedAt && (
            <Box display="flex" alignItems="center" gridGap="0.25rem">
              <Icon color="primary">picture_as_pdf</Icon>
              <Typography variant="body2">
                Installation Acceptance Document — generated {generatedAt.toLocaleTimeString()}
              </Typography>
            </Box>
          )}
          <Button
            variant="contained"
            color="primary"
            disabled={generating}
            onClick={handleGenerate}
            startIcon={generating ? <CircularProgress size={16} /> : <Icon>picture_as_pdf</Icon>}
          >
            {generating ? 'Generating…' : 'Generate Acceptance PDF'}
          </Button>
        </Box>
      </Box>

      <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} border={1} borderColor="divider">
        <Box className={classes.nav} py={1}>
          {sections.map(section => (
            <Box
              key={section.id}
              className={clsx(classes.navItem, { [classes.navItemSelected]: section.id === selectedId })}
              onClick={() => setSelectedId(section.id)}
            >
              {sectionStarted(section) ? (
                <Icon className={classes.navCheck}>check_circle</Icon>
              ) : (
                <Icon color="disabled">radio_button_unchecked</Icon>
              )}
              <Typography variant="body2">{section.name}</Typography>
            </Box>
          ))}
        </Box>

        <Box className={classes.content}>
          <Typography variant="h3" gutterBottom>
            {selected.name}
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {selected.description}
          </Typography>

          <Box mt={2}>
            <Grid container spacing={2}>
              {selected.fields.map(field => (
                <Grid item xs={12} sm={field.multiline ? 12 : 6} key={field.key}>
                  <TextField
                    fullWidth
                    size="small"
                    label={field.label}
                    type={field.type ?? 'text'}
                    multiline={field.multiline}
                    minRows={field.multiline ? 3 : undefined}
                    InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
                    value={values[selected.id]?.[field.key] ?? ''}
                    onChange={handleFieldChange(selected.id, field.key)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          {selected.hasPhotos && (
            <Box mt={3}>
              <Typography variant="subtitle2" gutterBottom>
                Photos
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Labels appear on the photos in the generated document (e.g. “PreOp 1”).
              </Typography>
              <Box display="flex" flexWrap="wrap" gridGap="0.75rem" alignItems="stretch">
                {selectedPhotos.map((photo, index) => (
                  <Box key={index} display="flex" flexDirection="column" gridGap="0.25rem">
                    <Box className={classes.thumbnail}>
                      {photo.isPdf ? (
                        <Tooltip title={photo.name}>
                          <Icon fontSize="large" color="primary">
                            picture_as_pdf
                          </Icon>
                        </Tooltip>
                      ) : (
                        <img src={photo.url} alt={photo.name} className={classes.thumbnailImage} />
                      )}
                      <IconButton
                        size="small"
                        className={classes.thumbnailDelete}
                        onClick={() => handleRemovePhoto(index)}
                      >
                        <Icon fontSize="small">close</Icon>
                      </IconButton>
                    </Box>
                    <TextField
                      size="small"
                      placeholder="Label (e.g. PreOp 1)"
                      value={photo.label}
                      onChange={handlePhotoLabelChange(index)}
                      className={classes.thumbnailLabel}
                      inputProps={{ 'aria-label': 'Photo label' }}
                    />
                  </Box>
                ))}
                <Box
                  className={classes.dropzone}
                  flex={selectedPhotos.length === 0 ? 1 : undefined}
                  minWidth="10rem"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  onClick={() => fileInput.current?.click()}
                >
                  <Icon color="primary">add_a_photo</Icon>
                  <Typography variant="body2" color="textSecondary">
                    Drag photos here or click to upload
                  </Typography>
                </Box>
              </Box>
              <input
                ref={fileInput}
                type="file"
                accept="image/*,application/pdf"
                multiple
                style={{ display: 'none' }}
                onChange={handleAddPhotos}
              />
            </Box>
          )}

          <Box mt={3} display="flex" justifyContent="space-between">
            <Button
              disabled={selectedIndex === 0}
              onClick={() => setSelectedId(sections[selectedIndex - 1].id)}
              startIcon={<Icon>chevron_left</Icon>}
            >
              Back
            </Button>
            <Button
              color="primary"
              disabled={selectedIndex === sections.length - 1}
              onClick={() => setSelectedId(sections[selectedIndex + 1].id)}
              endIcon={<Icon>chevron_right</Icon>}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default HospitalInstallation;
